export interface LabelledCount {
  label: string;
  count: number;
}

export interface MonthlyCount {
  month: string;
  count: number;
}

export interface MonthlyAmount {
  month: string;
  amount: number;
}

export interface SubscriptionFlow {
  month: string;
  created: number;
  cancelled: number;
  net: number;
}

export interface PlanRevenue {
  label: string;
  mrr: number;
  subscribers: number;
}

export interface DashboardTotals {
  users: number;
  newUsers30d: number;
  budgets: number;
  activeSubscriptions: number;
  payingCustomers: number;
  trials: number;
  mrr: number;
  previousMrr: number;
  arr: number;
  arpu: number;
  currency: string;
  churnRate: number;
  activationRate: number;
  usersWithBudgets: number;
}

export interface DashboardStats {
  totals: DashboardTotals;
  mrrByMonth: MonthlyAmount[];
  signupsByMonth: MonthlyCount[];
  subscriptionFlowByMonth: SubscriptionFlow[];
  subscriptionsByStatus: LabelledCount[];
  revenueByPlan: PlanRevenue[];
}

/** `past_due` → `Past due`, `active` → `Active`. */
export function humanize(label: string): string {
  const spaced = label.replace(/[_-]+/g, ' ').trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function sumCounts(items: LabelledCount[]): number {
  return items.reduce((total, item) => total + item.count, 0);
}

/** `$218` — whole units by default, since cents are noise at dashboard scale. */
export function formatMoney(amount: number, currency = 'USD', decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

export function formatPercent(ratio: number, decimals = 0): string {
  return `${(ratio * 100).toFixed(decimals)}%`;
}

/**
 * Growth from `previous` to `current`.
 *
 * Returns null when there is nothing to compare against — a percentage change
 * from zero is infinite, and "+∞%" is not a number anyone can act on.
 */
export function growthRate(current: number, previous: number): number | null {
  if (previous <= 0) {
    return null;
  }

  return (current - previous) / previous;
}
